import { Controller } from "stimulus"

export default class extends Controller {
  connect() {
    if (!("IntersectionObserver" in window)) {
      this.embedAllImmediately()
      return
    }

    this.observer = new IntersectionObserver(this.handleIntersect.bind(this), {
      rootMargin: "50px 0px",
    })

    this.links = this.element.querySelectorAll("a")

    this.links.forEach(link => {
      if (
        this.isYouTubeLink(link.href) ||
        this.isXPostLink(link.href) ||
        this.isInstagramLink(link.href) ||
        this.isLinkedInPostLink(link.href)
      ) {
        this.observer.observe(link)
      }
    })
  }

  disconnect() {
    this.observer?.disconnect()
  }

  handleIntersect(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const link = entry.target

        if (this.isYouTubeLink(link.href)) {
          this.embedYouTube(link)
        } else if (this.isXPostLink(link.href)) {
          this.embedXPost(link)
        } else if (this.isInstagramLink(link.href)) {
          this.embedInstagram(link)
        } else if (this.isLinkedInPostLink(link.href)) {
          this.embedLinkedIn(link)
        }

        this.observer.unobserve(link)
      }
    })
  }

  // === YouTube ===
  isYouTubeLink(url) {
    return /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)/.test(url)
  }

  extractYouTubeVideoId(url) {
    try {
      const parsedUrl = new URL(url)
      if (parsedUrl.hostname.includes("youtu.be")) {
        return parsedUrl.pathname.slice(1)
      } else if (parsedUrl.hostname.includes("youtube.com")) {
        return parsedUrl.searchParams.get("v")
      }
    } catch {
      return null
    }
    return null
  }

  embedYouTube(link) {
    const videoId = this.extractYouTubeVideoId(link.href)
    if (!videoId) return

    const iframe = document.createElement("iframe")
    iframe.src = `https://www.youtube.com/embed/${videoId}`
    iframe.width = "560"
    iframe.height = "315"
    iframe.classList.add("embed-responsive-item");
    iframe.frameBorder = "0"
    iframe.allow =
      "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
    iframe.allowFullscreen = true

    const iframeDiv = document.createElement("div")
    iframeDiv.classList.add('embed-responsive')
    iframeDiv.classList.add('embed-responsive-16by9')
    iframeDiv.appendChild(iframe)

    link.replaceWith(iframeDiv)
  }

  // === X (Twitter) ===
  isXPostLink(url) {
    return /^https?:\/\/(www\.)?(twitter\.com|x\.com)\/[^\/]+\/status\/\d+/.test(url)
  }

  embedXPost(link) {
    const blockquote = document.createElement("blockquote")
    blockquote.className = "twitter-tweet"

    const a = document.createElement("a")
		const tweetRegex = /https?:\/\/(?:www\.)?(?:twitter|x)\.com\/([a-zA-Z0-9_]+)\/status\/(\d+)/;
    const match = link.href.match(tweetRegex);
    const tweetId = match[2];
    a.href = `https://twitter.com/i/status/${tweetId}`

    blockquote.appendChild(a)

    link.replaceWith(blockquote)

    if (typeof twttr === "undefined") {
      this.loadScript("https://platform.twitter.com/widgets.js", () => {
        window.twttr?.widgets?.load()
      })
    } else {
      window.twttr.widgets.load()
    }
  }

  // === Instagram ===
  isInstagramLink(url) {
    return /^https?:\/\/(www\.)?instagram\.com\/(reel|p|tv)\/[a-zA-Z0-9_-]+/.test(url)
  }

  embedInstagram(link) {
    const blockquote = document.createElement("blockquote")
    blockquote.className = "instagram-media"
    blockquote.setAttribute("data-instgrm-permalink", link.href)
    blockquote.setAttribute("data-instgrm-version", "14")
    blockquote.style = "width: 100%; max-width: 540px; margin: auto;"

    const a = document.createElement("a")
    a.href = link.href
    blockquote.appendChild(a)

    link.replaceWith(blockquote)

    if (typeof instgrm === "undefined") {
      this.loadScript("https://www.instagram.com/embed.js", () => {
        window.instgrm?.Embeds?.process()
      })
    } else {
      window.instgrm.Embeds.process()
    }
  }

  // === LinkedIn ===
  isLinkedInPostLink(url) {
    return /activity-(\d+)-/.test(url)
  }

  embedLinkedIn(link) {
    const iframe = document.createElement("iframe")
    iframe.src = `https://www.linkedin.com/embed/feed/update/${this.extractLinkedInActivityId(link.href)}`
    iframe.width = "504"
    iframe.height = "600"
    iframe.frameBorder = "0"
    iframe.allowFullscreen = true
    iframe.title = "LinkedIn Post"
    iframe.style = "max-width: 100%; margin: auto;"

    link.replaceWith(iframe)
  }

  extractLinkedInActivityId(url) {
    try {
      const regex = /activity-(\d+)-/;
      const match = url.match(regex);
      return match ? match[1] : null;
    } catch {
      return null
    }
  }

  // === Script Loader ===
  loadScript(src, callback) {
    const existing = document.querySelector(`script[src="${src}"]`)
    if (existing) {
      if (callback) callback()
      return
    }

    const script = document.createElement("script")
    script.src = src
    script.async = true
    script.onload = callback
    document.body.appendChild(script)
  }

  // === Fallback for unsupported browsers ===
  embedAllImmediately() {
    this.links.forEach(link => {
      if (this.isYouTubeLink(link.href)) {
        this.embedYouTube(link)
      } else if (this.isXPostLink(link.href)) {
        this.embedXPost(link)
      } else if (this.isInstagramLink(link.href)) {
        this.embedInstagram(link)
      } else if (this.isLinkedInPostLink(link.href)) {
        this.embedLinkedIn(link)
      }
    })
  }
}
